/**
 * The extraction reports the fields it filled by their internal names, which is what the pipeline
 * records but not what a rep reads. Each one is labeled in the vocabulary the quote form already
 * uses — "Origin address" rather than originInlandName, "Containers" rather than equipment.
 *
 * Anything not listed falls back to humanizeField(), so a field added on the backend shows up as
 * readable text instead of camelCase while it waits for a label of its own.
 */
const CONTRIBUTED_FIELD_LABELS: Record<string, string> = {
  client: "Client",
  destination: "Destination location",
  destinationCountry: "Destination country",
  destinationInlandName: "Destination address",
  destinationPostalCode: "Destination postal code",
  direction: "Direction",
  equipment: "Containers",
  incoterms: "Incoterms",
  insurance: "Insured cargo value",
  insuranceCurrency: "Insurance currency",
  ldm: "Loading meters",
  measurement: "Volume",
  origin: "Origin location",
  originCountry: "Origin country",
  originInlandName: "Origin address",
  originPostalCode: "Origin postal code",
  packages: "Packages",
  pickupDate: "Pickup date",
  pieces: "Pieces",
  showGrandTotal: "Grand total",
  type: "Quote type",
  weight: "Weight",
};

function humanizeField(field: string): string {
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * One file that arrived with the inbound email a quote came from.
 *
 * A row outlives the file it describes: once the retention window passes or someone deletes it, the
 * name, type and contributed fields stay behind so a year-old quote can still say which attachment
 * a weight was read out of.
 */
export class QuoteEmailAttachment {
  constructor(
    private id: number = null,
    private filename: string = null,
    private sizeBytes: number = 0,
    private mimeType: string = null,
    private state: string = null,
    private classification: string = null,
    private classificationReason: string = null,
    private scanVerdict: string = null,
    private contributedFields: string[] = [],
    private available: boolean = false,
    private parentAttachmentId: number = null,
    private deleteReason: string = null,
  ) {}

  public getId(): number {
    return this.id;
  }

  public getFilename(): string {
    return this.filename;
  }

  public getSizeBytes(): number {
    return this.sizeBytes;
  }

  public getMimeType(): string {
    return this.mimeType;
  }

  public getClassificationReason(): string {
    return this.classificationReason;
  }

  public getContributedFields(): string[] {
    return this.contributedFields ?? [];
  }

  public getContributedFieldLabels(): string[] {
    return this.getContributedFields().map(
      (field) => CONTRIBUTED_FIELD_LABELS[field] ?? humanizeField(field),
    );
  }

  public getParentAttachmentId(): number {
    return this.parentAttachmentId;
  }

  public isChild(): boolean {
    return this.parentAttachmentId !== null;
  }

  /** Still being scanned, expanded, classified or converted. */
  public isProcessing(): boolean {
    return [
      "stored",
      "scanning",
      "expanding",
      "classifying",
      "converting",
    ].includes(this.state);
  }

  public isInfected(): boolean {
    return this.state === "infected";
  }

  public isArchive(): boolean {
    return this.classification === "container";
  }

  public wasUsedByAi(): boolean {
    return this.getContributedFields().length > 0;
  }

  /**
   * No scanner is configured, so nothing looked at this file. Shown explicitly rather than left
   * silent — saying nothing would imply a guarantee the app is not making.
   */
  public wasNotScanned(): boolean {
    return this.scanVerdict === "not_scanned";
  }

  /** Removed by a person, which reads very differently from having aged out. */
  public isDeleted(): boolean {
    return this.state === "deleted";
  }

  /** Its retention window closed and the sweeper removed the stored file. */
  public isExpired(): boolean {
    return this.isGone() && !this.isDeleted();
  }

  public getDeleteReason(): string {
    return this.deleteReason;
  }

  /**
   * The file itself is gone, either way. Judged on the server from the row rather than from
   * storage, so the app never offers a file it has called expired.
   */
  public isGone(): boolean {
    return !this.available && !this.isProcessing() && !this.isInfected();
  }

  /**
   * Reflects a deletion the user just made, so the row changes in place instead of vanishing and
   * reappearing in a different form on the next load.
   */
  public applyDeletion(reasonLabel: string, maskedFilename: string): void {
    this.state = "deleted";
    this.available = false;
    this.deleteReason = reasonLabel;
    this.filename = maskedFilename ?? this.filename;
  }

  public canBePreviewed(): boolean {
    return this.available && !this.isArchive();
  }
}
